import React, { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useBdApiClient, useBdApiContext, clearBdCache } from "../hooks/useBdApi";
import { formatDateTime, relativeTime } from "../metrics";

const POLL_MS = 4000;

/**
 * "Last synced x ago" badge with a manual refresh button. Polls the sync
 * status while a run is in progress, then bumps the data version so every
 * view refetches.
 */
export default function SyncStatus({ initial }) {
  const api = useBdApiClient();
  const { bump } = useBdApiContext();
  const [status, setStatus] = useState(initial || null);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    if (initial) setStatus(initial);
  }, [initial]);

  const fetchStatus = useCallback(async () => {
    const res = await api.get("landmark/bd/sync/status");
    if (res && res.success) {
      setStatus(res);
      return res;
    }
    return null;
  }, [api]);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const startPolling = useCallback(() => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      const res = await fetchStatus();
      if (res && !res.running) {
        stopPolling();
        setBusy(false);
        clearBdCache();
        bump();
        if (res.lastRun && res.lastRun.status === "success") {
          toast.success("Salesforce data refreshed");
        } else if (res.lastRun && res.lastRun.status === "error") {
          toast.error(`Refresh failed: ${res.lastRun.error || "unknown error"}`);
        }
      }
    }, POLL_MS);
  }, [fetchStatus, bump]);

  useEffect(() => {
    if (status && status.running && !pollRef.current) {
      setBusy(true);
      startPolling();
    }
    return stopPolling;
  }, [status, startPolling]);

  const refresh = async () => {
    if (busy) return;
    setBusy(true);
    const res = await api.post("landmark/bd/sync", {});
    if (!res || res.success === false) {
      setBusy(false);
      toast.error((res && res.error) || "Could not start refresh");
      return;
    }
    toast("Refreshing from Salesforce…", { icon: "⟳" });
    startPolling();
  };

  const last = status && (status.lastSuccess || status.lastRun);
  const lastAt = last && (last.finishedAt || last.startedAt);
  const running = busy || (status && status.running);

  return (
    <div className="bd-sync">
      <span className="bd-sync__label" title={lastAt ? `Last synced ${formatDateTime(lastAt)}` : "Not synced yet"}>
        <i className={`bd-sync__dot ${running ? "is-running" : last && last.status === "error" ? "is-error" : ""}`} />
        {running ? "Syncing…" : `Synced ${relativeTime(lastAt)}`}
      </span>
      <button type="button" className="bd-btn bd-btn--secondary bd-sync__btn" onClick={refresh} disabled={running} title="Pull the latest data from Salesforce">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={running ? "spin" : ""}>
          <path d="M21 12a9 9 0 1 1-2.64-6.36" />
          <path d="M21 3v6h-6" />
        </svg>
        Refresh
      </button>
    </div>
  );
}
